// src/tenantAdmin/resendThrottle.js
//
// The client-side half of the invitation rate limit: 3 sends per 10 minutes PER
// ADDRESS. The backend enforces its own (20/h per admin AND 3 per 10 min per
// address, shared by invite, attach and resend) and answers 429 RATE_LIMITED
// with `retry_after_seconds`.
//
// ⚠️ So why count here at all? Because the server's refusal arrives AFTER the
// admin has confirmed a dialog that promised "a new link will be sent and the
// previous one stops working" — and at that point neither sentence is true. This
// counter lets the fourth attempt be refused BEFORE the confirm, in our own
// words, while the 429 stays mapped as the authority.
//
// ⛔ It counts what WE sent from this browser tab, which is a subset of what the
// server counted (another admin, another tab, another device). It therefore only
// ever refuses early — it can never grant a send the server will refuse.
//
// ⚠️ Every send against the shared per-address limit records here, not just a
// resend: the add-user dialog's invite and attach consume the same budget.
//
// ⛔ sessionStorage may be unavailable (blocked cookies/site data, Safari private
// mode) or full, and both read and write can THROW rather than return null. Every
// access is wrapped, and a failure never breaks the control: the in-memory map
// below keeps the count for the lifetime of the page, and if even that is lost
// the caller simply sends and lets the server answer.

const KEY = 'tenant_admin_invite_sends';
const WINDOW_MS = 10 * 60 * 1000;
const MAX_SENDS = 3;

// The fallback store. It is written on EVERY record (cheap) and read only when
// sessionStorage is unavailable or unparseable, so the two can never disagree
// about which one is in charge.
let memory = {};

// One address, one bucket. Lower-cased and trimmed because the backend treats
// addresses case-insensitively and the admin may type either.
const normalize = (email) => String(email || '').trim().toLowerCase();

function readAll() {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Blocked, unavailable or corrupt — the in-memory count is what we have.
    return memory;
  }
}

function writeAll(map) {
  memory = map;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    // Full or blocked. `memory` already carries it; nothing to report to the
    // user, who asked to send an invitation and not to manage a quota.
  }
}

// Timestamps inside the window, newest order irrelevant. Pruning on read keeps
// the stored object from growing for the whole session.
function recentFor(map, address, now) {
  const stamps = Array.isArray(map[address]) ? map[address] : [];
  return stamps.filter(ts => Number.isFinite(ts) && now - ts < WINDOW_MS);
}

/**
 * May we send to this address again right now?
 *
 * @param {string} email the recipient
 * @returns {boolean} false only when WE have already sent MAX_SENDS inside the
 *   window; unknown/unreadable state answers true (the server is the gate).
 */
export function canSend(email) {
  const address = normalize(email);
  if (!address) return true;
  return recentFor(readAll(), address, Date.now()).length < MAX_SENDS;
}

/**
 * Record a request the server ACCEPTED (an invite, an attach or a resend).
 *
 * ⚠️ Accepted, not delivered: an `email_sent: false` answer still rotated the
 * link and still spent the address's server-side budget, so it counts.
 * ⛔ A REFUSED call does not: nothing was spent, and counting it would lock the
 * admin out of the retry they are entitled to.
 *
 * @param {string} email the recipient
 */
export function record(email) {
  const address = normalize(email);
  if (!address) return;
  const now = Date.now();
  const all = readAll();
  // Prune every address, not just this one: the map is the only thing that grows.
  const next = {};
  Object.keys(all).forEach(key => {
    const kept = recentFor(all, key, now);
    if (kept.length) next[key] = kept;
  });
  next[address] = [...(next[address] || []), now];
  writeAll(next);
}

/**
 * Forget every recorded send. For tests and for a sign-out — the counter is
 * about this browser, not about the account.
 */
export function clearRecords() {
  memory = {};
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Nothing stored means nothing to remove.
  }
}

export { MAX_SENDS, WINDOW_MS };
